import '../../scrollytelling.css'
import './lanes.css'

import clsx from 'clsx'
import PropTypes from 'prop-types'
import { Fragment, useEffect, useRef } from 'react'

import { Shape } from '../play/shapes'
import { FLOWS, LANES, POINTS, STEPS } from './steps'
import { UI } from './ui'

const clamp = (v, a = 0, b = 1) => Math.min(b, Math.max(a, v))
const DEMO = 'https://www.aivy.app/gespraech-vereinbaren'

function Point({ kind, text }) {
  const p = POINTS[kind]
  return (
    <span className='ln-point'>
      <Shape kind={p.shape} color={p.color} size={14} />
      <span>
        <b>{p.label}</b> {text}
      </span>
    </span>
  )
}

Point.propTypes = { kind: PropTypes.string.isRequired, text: PropTypes.string }

function Card({ step, index }) {
  const Ui = UI[step.ui]
  return (
    <article className='ln-card'>
      <header className='ln-card__head'>
        <span className='ln-card__n'>{String(index + 1).padStart(2, '0')}</span>
        <span className='ln-card__title'>{step.title}</span>
        {step.optional && (
          <span className='ln-badge ln-badge--opt'>Optional</span>
        )}
      </header>
      <p className='ln-card__text'>{step.text}</p>
      <div className='ln-window'>
        <Ui />
      </div>
      {step.points.length > 0 && (
        <div className='ln-points'>
          {step.points.map(([kind, text], i) => (
            <span key={text} className='ln-points__i' style={{ '--k': i }}>
              <Point kind={kind} text={text} />
            </span>
          ))}
        </div>
      )}
    </article>
  )
}

Card.propTypes = {
  step: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired
}

/**
 * Version „Aivy Spalten“: Fachabteilung, HR und Talent laufen nebeneinander
 * mit. Die Zeile in der Bildmitte ist im Fokus, die Spalte, die gerade etwas
 * tun muss, wird hervorgehoben. Linien zeigen, welche Daten wohin wandern,
 * Punkte zeigen, wo es automatisch, fairer, schneller oder datenbasiert wird.
 */
export default function AivyLanes() {
  const rootRef = useRef(null)
  const bodyRef = useRef(null)
  const svgRef = useRef(null)

  useEffect(() => {
    const root = rootRef.current
    const body = bodyRef.current
    const svg = svgRef.current
    if (!root || !body || !svg) return undefined
    const rows = Array.from(body.querySelectorAll('[data-step]'))
    const heads = Array.from(root.querySelectorAll('[data-lane-head]'))
    const counters = Array.from(root.querySelectorAll('[data-count]'))
    const flows = FLOWS.map((f) => ({
      ...f,
      path: svg.querySelector(`[data-flow="${f.from}"] path`),
      packet: svg.querySelector(`[data-flow="${f.from}"] .ln-packet`),
      len: 0
    }))
    let raf = 0
    let geom = null

    // Linien zwischen den Karten, in Koordinaten des Zeilen-Containers
    const measure = () => {
      const box = body.getBoundingClientRect()
      svg.setAttribute('width', box.width)
      svg.setAttribute('height', box.height)
      svg.setAttribute('viewBox', `0 0 ${box.width} ${box.height}`)
      const cardOf = (id) => {
        const el = body.querySelector(`[data-step="${id}"] .ln-card`)
        const r = el.getBoundingClientRect()
        return {
          x: r.left - box.left + r.width / 2,
          top: r.top - box.top,
          bottom: r.bottom - box.top,
          left: r.left - box.left,
          right: r.right - box.left
        }
      }
      flows.forEach((f) => {
        const a = cardOf(f.from)
        const b = cardOf(f.to)
        const sameLane = Math.abs(a.x - b.x) < 4
        const x0 = sameLane ? a.right - 24 : a.x
        const x1 = sameLane ? b.right - 24 : b.x
        const y0 = a.bottom
        const y1 = b.top
        const mid = (y0 + y1) / 2
        f.path.setAttribute(
          'd',
          `M${x0},${y0} C${x0},${mid} ${x1},${mid} ${x1},${y1}`
        )
        f.len = f.path.getTotalLength()
        f.path.style.strokeDasharray = `${f.len}`
      })
      geom = { top: box.top + window.scrollY }
    }

    const tick = () => {
      raf = 0
      if (!geom) measure()
      const vh = window.innerHeight
      const vc = vh * 0.5
      let best = -1
      let bestF = 0
      const centers = {}
      const counts = {}
      rows.forEach((row, i) => {
        const r = row.getBoundingClientRect()
        const c = r.top + r.height / 2
        centers[STEPS[i].id] = c
        const f = 1 - clamp(Math.abs(c - vc) / (vh * 0.55))
        const p = clamp((vc - r.top) / r.height)
        row.style.setProperty('--f', f.toFixed(3))
        row.style.setProperty('--p', p.toFixed(3))
        if (f > bestF) {
          bestF = f
          best = i
        }
        if (p > 0.3)
          STEPS[i].points.forEach(([k]) => (counts[k] = (counts[k] || 0) + 1))
      })
      const lane = best >= 0 ? STEPS[best].lane : null
      root.dataset.active = lane || ''
      rows.forEach((row, i) => row.classList.toggle('is-active', i === best))
      heads.forEach((h) =>
        h.classList.toggle('is-on', h.dataset.laneHead === lane)
      )
      counters.forEach((n) => {
        n.textContent = String(counts[n.dataset.count] || 0)
      })
      const total = STEPS.length - 1
      root.style.setProperty('--story', clamp(best / total).toFixed(3))

      // Datenpaket wandert, während die Bildmitte von Karte zu Karte geht
      flows.forEach((f) => {
        const a = centers[f.from]
        const b = centers[f.to]
        const g = clamp((vc - a) / (b - a))
        f.path.style.strokeDashoffset = `${(f.len * (1 - g)).toFixed(1)}`
        const show = g > 0.02 && g < 0.98
        f.packet.style.opacity = show ? '1' : '0'
        if (show) {
          const pt = f.path.getPointAtLength(f.len * g)
          f.packet.setAttribute(
            'transform',
            `translate(${pt.x.toFixed(1)} ${pt.y.toFixed(1)})`
          )
        }
      })
    }

    const schedule = () => {
      if (!raf) raf = requestAnimationFrame(tick)
    }
    const remeasure = () => {
      geom = null
      schedule()
    }
    const ro = new ResizeObserver(remeasure)
    ro.observe(body)
    document.fonts?.ready.then(remeasure)
    window.addEventListener('scroll', schedule, { passive: true })
    window.addEventListener('resize', remeasure)
    schedule()
    return () => {
      cancelAnimationFrame(raf)
      ro.disconnect()
      window.removeEventListener('scroll', schedule)
      window.removeEventListener('resize', remeasure)
    }
  }, [])

  const laterAt = STEPS.findIndex((s) => s.later)

  return (
    <div ref={rootRef} className='sty-root ln-root'>
      <section className='ln-intro'>
        <span className='ln-eyebrow'>Wer macht wann was</span>
        <h1 className='ln-h1'>Drei Rollen. Ein Prozess.</h1>
        <p className='ln-lead'>
          Fachabteilung, HR und Talent laufen nebeneinander mit. Scroll, und du
          siehst, wer gerade dran ist, welche Daten wohin wandern und wo es
          automatisch, fairer oder schneller wird.
        </p>
        <span className='ln-legend-pts'>
          {Object.entries(POINTS).map(([k, p]) => (
            <span key={k}>
              <Shape kind={p.shape} color={p.color} size={14} />
              {p.label}
            </span>
          ))}
        </span>
      </section>

      <div className='ln-top'>
        <div className='ln-score'>
          {Object.entries(POINTS).map(([k, p]) => (
            <span key={k} className='ln-score__i'>
              <Shape kind={p.shape} color={p.color} size={12} />
              <b data-count={k}>0</b>
              {p.label}
            </span>
          ))}
        </div>
        <div className='ln-lanes-head'>
          {LANES.map((l) => (
            <div key={l.id} className='ln-lane-head' data-lane-head={l.id}>
              <span className='ln-lane-head__t'>{l.title}</span>
              <span className='ln-lane-head__p'>
                {l.person} · {l.role}
              </span>
              <span className='ln-lane-head__on'>ist dran</span>
            </div>
          ))}
        </div>
        <span className='ln-top__bar' />
      </div>

      <div ref={bodyRef} className='ln-body'>
        <div className='ln-cols' aria-hidden='true'>
          {LANES.map((l) => (
            <span key={l.id} className='ln-col' data-col={l.id} />
          ))}
        </div>
        <svg ref={svgRef} className='ln-flows' aria-hidden='true'>
          {FLOWS.map((f) => (
            <g key={f.from} data-flow={f.from}>
              <path className='ln-flow' />
              <g className='ln-packet'>
                <circle r='6' />
                <text x='12' y='4'>
                  {f.label}
                </text>
              </g>
            </g>
          ))}
        </svg>

        {STEPS.map((step, i) => (
          <Fragment key={step.id}>
            {i === laterAt && (
              <div className='ln-later'>
                <span>Auf Dauer</span>
                Was nach der Einstellung mit den Daten passiert
              </div>
            )}
            <div
              className={clsx('ln-row', `ln-row--${step.lane}`)}
              data-step={step.id}
            >
              {LANES.map((l) =>
                l.id === step.lane ? (
                  <div key={l.id} className='ln-cell ln-cell--act'>
                    <span className='ln-cell__lane'>{l.title}</span>
                    <Card step={step} index={i} />
                  </div>
                ) : (
                  <div key={l.id} className='ln-cell ln-cell--idle'>
                    <span className='ln-idle'>
                      <i />
                      {step.idle[l.id]}
                    </span>
                  </div>
                )
              )}
            </div>
          </Fragment>
        ))}
      </div>

      <section className='ln-outro'>
        <h2 className='ln-h2'>Jede Rolle macht nur, was sie wirklich muss.</h2>
        <p className='ln-lead'>
          Der Rest passiert automatisch, und die Daten aus jeder Einstellung
          machen die nächste besser.
        </p>
        <span className='ln-score ln-score--end'>
          {Object.entries(POINTS).map(([k, p]) => (
            <span key={k} className='ln-score__i'>
              <Shape kind={p.shape} color={p.color} size={14} />
              <b>
                {STEPS.reduce(
                  (n, s) => n + s.points.filter(([pk]) => pk === k).length,
                  0
                )}
              </b>
              {p.label}
            </span>
          ))}
        </span>
        <a className='ln-cta' href={DEMO} target='_blank' rel='noreferrer'>
          Demo buchen
        </a>
      </section>
    </div>
  )
}
